<template>
  <el-dialog
    :visible.sync="dialogVisible"
    fullscreen
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    custom-class="industry-position-dialog"
    append-to-body
  >
    <div class="industry-position">
      <!-- 顶部标题栏 -->
      <div class="top-header">
        <div class="title-row">
          <i class="el-icon-arrow-left back-home-icon" @click="goHome"></i>
          <h3 class="dialog-title">行业岗位设置</h3>
        </div>
        <p class="dialog-desc">请选择您企业涉及的行业和岗位，以便获得更精准的匹配服务</p>
      </div>

      <!-- 主内容区 -->
      <div class="main-content" v-loading="loading">
        <!-- 左侧行业大类 -->
        <div class="left-panel">
          <div class="panel-title">行业分类</div>
          <div class="category-list">
            <div
              v-for="industry in industries"
              :key="industry.id"
              class="category-item"
              :class="{ active: selectedIndustryIds.includes(industry.id) }"
            >
              <el-checkbox
                :value="selectedIndustryIds.includes(industry.id)"
                @change="(val) => toggleIndustry(industry.id, val)"
              >{{ industry.name }}</el-checkbox>
            </div>
          </div>
        </div>

        <!-- 右侧子分类 -->
        <div class="right-panel">
          <template v-if="visibleSubGroups.length > 0">
            <div
              v-for="group in visibleSubGroups"
              :key="group.id"
              class="sub-group"
            >
              <div class="sub-group-title">
                <span class="group-name">{{ group.name }}</span>
                <span class="group-count">{{ getGroupSelectedCount(group) }}/{{ group.items.length }}</span>
              </div>
              <div class="sub-group-items">
                <div
                  v-for="item in group.items"
                  :key="item.id"
                  class="item-btn"
                  :class="{ selected: isItemSelected(item) }"
                  @click="toggleItem(item, group)"
                >
                  {{ item.name }}
                </div>
              </div>
            </div>
          </template>
          <div v-else class="empty-tip">
            <i class="el-icon-folder-opened"></i>
            <p>请在左侧勾选行业，再选择对应岗位</p>
          </div>
        </div>
      </div>

      <!-- 底部已选栏 -->
      <div class="bottom-bar">
        <div class="selected-area">
          <span class="selected-label">已选：</span>
          <div class="selected-tags" v-if="selectedItems.length > 0">
            <el-tag
              v-for="item in selectedItems"
              :key="item.id"
              closable
              size="small"
              @close="removeItem(item)"
            >{{ item.name }}</el-tag>
          </div>
          <span v-else class="no-selection">暂无选择</span>
        </div>
        <div class="bottom-actions">
          <el-button size="small" icon="el-icon-refresh" @click="handleReset">重置</el-button>
          <el-button type="primary" size="small" icon="el-icon-check" :loading="submitting" @click="handleSubmit">提交</el-button>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
// 行业-子分类-岗位 三级数据
const INDUSTRY_DATA = []

export default {
  name: 'IndustryPosition',
  props: {
    visible: { type: Boolean, default: false },
    returnToCurrentPage: { type: Boolean, default: false },
    // 初始已选数据 [{ id, name }]
    initSelected: { type: Array, default: () => [] }
  },
  data() {
    return {
      dialogVisible: false,
      industries: INDUSTRY_DATA,
      loading: false,
      submitting: false,
      selectedIndustryIds: [],
      selectedItems: [],
      originalSelectedIds: [],
      originalSelectedItems: []
    }
  },
  computed: {
    visibleSubGroups() {
      const groups = []
      this.selectedIndustryIds.forEach(indId => {
        const industry = this.industries.find(i => i.id === indId)
        if (industry) {
          industry.subGroups.forEach(sg => {
            groups.push({ ...sg, industryId: industry.id, industryName: industry.name })
          })
        }
      })
      return groups
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) this.openDialog()
    },
    dialogVisible(val) {
      if (!val) this.$emit('update:visible', false)
    }
  },
  methods: {
    openDialog() {
      this.loadIndustryData().then(() => this.loadSelectedSceneWork())
    },
    loadIndustryData() {
      this.loading = true
      return this.$Api.industrySceneWorkTree().then((res) => {
        const result = res && res.code !== undefined ? res : res.data
        if (result.code === 200) {
          const list = (result.data && result.data.list) || []
          this.industries = this.normalizeIndustryTree(list)
        } else {
          this.$message.error(result.msg || '获取行业岗位数据失败')
        }
      }).catch(() => {
        this.$message.error('获取行业岗位数据失败')
      }).finally(() => {
        this.loading = false
      })
    },
    normalizeIndustryTree(list) {
      return (list || []).map(industry => ({
        id: industry.value,
        name: industry.label,
        subGroups: (industry.children || []).map(group => ({
          id: group.value,
          name: group.label,
          items: (group.children || []).map(item => ({
            id: item.value,
            name: item.label
          }))
        }))
      }))
    },
    loadSelectedSceneWork() {
      return this.$Api.getSceneWork().then((res) => {
        const result = res && res.code !== undefined ? res : res.data
        if (result.code === 200) {
          const list = (result.data && result.data.list) || []
          this.restoreSelectedByTree(list)
        } else {
          this.$message.error(result.msg || '获取已选岗位失败')
          this.restoreInitData()
        }
      }).catch(() => {
        this.$message.error('获取已选岗位失败')
        this.restoreInitData()
      })
    },
    restoreSelectedByTree(list) {
      const selectedIds = new Set()
      this.collectLeafIds(list, selectedIds)

      if (selectedIds.size === 0) {
        this.restoreInitData()
        return
      }

      const selectedItems = []
      const selectedIndustryIds = new Set()
      this.industries.forEach(industry => {
        industry.subGroups.forEach(group => {
          group.items.forEach(item => {
            if (selectedIds.has(String(item.id))) {
              selectedIndustryIds.add(industry.id)
              selectedItems.push({
                id: item.id,
                name: item.name,
                groupId: group.id,
                groupName: group.name,
                industryId: industry.id,
                industryName: industry.name
              })
            }
          })
        })
      })
      this.selectedItems = selectedItems
      this.selectedIndustryIds = [...selectedIndustryIds]
      this.syncOriginalSelectedIds()
    },
    collectLeafIds(nodes, selectedIds) {
      (nodes || []).forEach(node => {
        const children = node.children || []
        if (children.length > 0) {
          this.collectLeafIds(children, selectedIds)
        } else if (node.value !== undefined && node.value !== null) {
          selectedIds.add(String(node.value))
        }
      })
    },
    restoreInitData() {
      if (this.initSelected && this.initSelected.length > 0) {
        this.selectedItems = [...this.initSelected]
        // 根据已选项反推已选行业
        const indIds = new Set()
        this.initSelected.forEach(item => {
          this.industries.forEach(ind => {
            ind.subGroups.forEach(sg => {
              if (sg.items.some(i => i.id === item.id)) {
                indIds.add(ind.id)
              }
            })
          })
        })
        this.selectedIndustryIds = [...indIds]
      } else {
        this.selectedItems = []
        this.selectedIndustryIds = []
      }
      this.syncOriginalSelectedIds()
    },
    syncOriginalSelectedIds() {
      this.originalSelectedIds = this.selectedItems.map(item => String(item.id))
      this.originalSelectedItems = this.selectedItems.map(item => ({ id: item.id }))
    },
    toggleIndustry(indId, checked) {
      if (checked) {
        if (!this.selectedIndustryIds.includes(indId)) {
          this.selectedIndustryIds.push(indId)
        }
      } else {
        this.selectedIndustryIds = this.selectedIndustryIds.filter(id => id !== indId)
        // 取消行业时，移除该行业下所有已选岗位
        const industry = this.industries.find(i => i.id === indId)
        if (industry) {
          const allItemIds = []
          industry.subGroups.forEach(sg => sg.items.forEach(item => allItemIds.push(item.id)))
          this.selectedItems = this.selectedItems.filter(s => !allItemIds.includes(s.id))
        }
      }
    },
    toggleItem(item, group) {
      const idx = this.selectedItems.findIndex(s => String(s.id) === String(item.id))
      if (idx > -1) {
        this.selectedItems.splice(idx, 1)
      } else {
        this.selectedItems.push({
          id: item.id,
          name: item.name,
          groupId: group.id,
          groupName: group.name,
          industryId: group.industryId,
          industryName: group.industryName
        })
      }
    },
    removeItem(item) {
      this.selectedItems = this.selectedItems.filter(s => String(s.id) !== String(item.id))
    },
    isItemSelected(item) {
      return this.selectedItems.some(s => String(s.id) === String(item.id))
    },
    getGroupSelectedCount(group) {
      return this.selectedItems.filter(s => group.items.some(i => String(i.id) === String(s.id))).length
    },
    handleReset() {
      this.selectedIndustryIds = []
      this.selectedItems = []
    },
    goHome() {
      this.dialogVisible = false
      if (!this.returnToCurrentPage && this.$route.path !== '/home') {
        this.$router.push('/home')
      }
    },
    handleSubmit() {
      const currentSelectedIdSet = new Set(this.selectedItems.map(item => String(item.id)))
      const originalSelectedIdSet = new Set(this.originalSelectedIds)
      const addedWorks = this.selectedItems
        .filter(item => !originalSelectedIdSet.has(String(item.id)))
        .map(item => ({
          work_type_id: item.id
        }))
      const deletedWorks = this.originalSelectedItems
        .filter(item => !currentSelectedIdSet.has(String(item.id)))
        .map(item => ({
          work_type_id: item.id,
          is_delete: 1
        }))
      const works = [...addedWorks, ...deletedWorks]

      const params = {
        works
      }
      this.submitting = true
      if (works.length === 0) {
        this.$message.success('提交成功')
        this.dialogVisible = false
        this.submitting = false
        return
      }
      this.$Api.saveSceneWork(params).then((res) => {
        const result = res && res.code !== undefined ? res : res.data
        if (result.code === 200) {
          this.$message.success(result.msg || '提交成功')
          this.refreshProfile().finally(() => {
            this.$emit('confirm', this.selectedItems)
            this.dialogVisible = false
            if (this.$route.path !== '/home') {
              this.$router.push('/home').catch(() => {})
            }
          })
        } else {
          this.$message.error(result.msg || '提交失败')
        }
      }).catch(() => {
        this.$message.error('提交失败')
      }).finally(() => {
        this.submitting = false
      })
    },
    refreshProfile() {
      return this.$Api.getProfile().then((res) => {
        const result = res && res.code !== undefined ? res : res.data
        if (result.code === 200) {
          const data = { ...(result.data || {}) }
          let cacheUserInfo = {}
          try {
            cacheUserInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
          } catch (e) {
            cacheUserInfo = {}
          }
          if (!data.auth && cacheUserInfo.auth) {
            data.auth = cacheUserInfo.auth
          }
          this.$store.commit('user/SET_USER_INFO', data)
          this.$root.$emit('profile-updated', data)
        }
      })
    }
  }
}
</script>

<style lang="less" scoped>
::v-deep .industry-position-dialog {
  .el-dialog__header {
    display: none;
  }
  .el-dialog__body {
    padding: 0;
    height: 100vh;
  }
  .el-dialog {
    margin: 0 !important;
    border-radius: 0;
  }
}

.industry-position {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--bg-page);
}

/* 顶部标题 */
.top-header {
  padding: 24px 32px 16px;
  background: var(--bg-card);
  border-bottom: 1px solid var(--border-light);
  flex-shrink: 0;

  .title-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .back-home-icon {
    font-size: 18px;
    font-weight: 700;
    color: var(--primary);
    cursor: pointer;
    line-height: 1;

    &:hover {
      color: var(--primary-hover);
    }
  }

  .dialog-title {
    font-size: 20px;
    font-weight: 600;
    color: var(--text-title);
    margin: 0 0 6px;
  }

  .dialog-desc {
    font-size: 14px;
    color: var(--text-secondary);
    margin: 0;
  }
}

/* 主内容 */
.main-content {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 16px;
  padding: 16px 32px;
  overflow: hidden;
}

/* 左侧行业 */
.left-panel {
  width: 220px;
  flex-shrink: 0;
  background: var(--bg-card);
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .panel-title {
    padding: 14px 16px;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-title);
    border-bottom: 1px solid var(--border-light);
    flex-shrink: 0;
  }

  .category-list {
    flex: 1;
    overflow-y: auto;
    padding: 8px 0;
  }

  .category-item {
    padding: 10px 16px;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: var(--bg-hover);
    }

    &.active {
      background: var(--primary-light);
    }

    /deep/ .el-checkbox {
      width: 100%;
      .el-checkbox__label {
        font-size: 14px;
        color: var(--text-body);
      }
    }
  }
}

/* 右侧子分类 */
.right-panel {
  flex: 1;
  min-width: 0;
  background: var(--bg-card);
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  overflow-y: auto;
  padding: 20px 24px;
}

.sub-group {
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
}

.sub-group-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;

  .group-name {
    font-size: 15px;
    font-weight: 600;
    color: var(--text-title);
  }

  .group-count {
    font-size: 12px;
    color: var(--text-secondary);
    background: var(--bg-page);
    padding: 1px 8px;
    border-radius: 10px;
  }
}

.sub-group-items {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.item-btn {
  padding: 7px 18px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid var(--border-color);
  color: var(--text-body);
  background: var(--bg-card);
  transition: all 0.2s;
  user-select: none;

  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }

  &.selected {
    background: var(--primary-light);
    border-color: var(--primary);
    color: var(--primary);
    font-weight: 500;
  }
}

.empty-tip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--text-placeholder);

  i {
    font-size: 48px;
    margin-bottom: 12px;
  }

  p {
    font-size: 14px;
  }
}

/* 底部已选栏 */
.bottom-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 32px;
  background: var(--bg-card);
  border-top: 1px solid var(--border-light);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
  flex-shrink: 0;
}

.selected-area {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;

  .selected-label {
    font-size: 14px;
    color: var(--text-secondary);
    flex-shrink: 0;
  }

  .selected-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;

    /deep/ .el-tag {
      border-color: var(--primary);
      color: var(--primary);
      background: var(--primary-light);

      .el-tag__close {
        color: var(--primary);
        &:hover {
          background: var(--primary);
          color: #fff;
        }
      }
    }
  }

  .no-selection {
    font-size: 13px;
    color: var(--text-placeholder);
  }
}

.bottom-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
  margin-left: 16px;
}
</style>
